import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import { IoMdAdd } from "react-icons/io";

const GraphicDesignerList = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  let is_alert = false;

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [monthFilter, setMonthFilter] = useState("");

  const isAlertShown = useRef(false);
  useEffect(() => {
    if (location.state && !isAlertShown.current) {
      toast.success(location.state);
      isAlertShown.current = true;
      window.history.replaceState({}, null);
    }
  }, [location.state]);

  useEffect(() => {
    fetchData();
  }, [page, search, monthFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const monthQuery = monthFilter ? `&month=${monthFilter}` : "";
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/admin/graphic_designer?page=${page}&search=${search}${monthQuery}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );
      setData(response.data.data.designers);
      setTotalPage(response.data.data.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching graphic designers:", error);
      if (error?.response?.data?.message === "Unauthorized!") {
        Navigate("/");
      }
      toast.error("Failed to fetch graphic designers");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await axios.delete(
          `${process.env.REACT_APP_URL}/admin/graphic_designer/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );
        toast.success("Graphic designer deleted successfully");
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting graphic designer:", error);
      toast.error("Failed to delete graphic designer");
    }
  };

  const fetchGDDesigns = async (gdId, gdName) => {
    try {
      Navigate(`/admin/graphic-designer/designs/${gdId}`, {
        state: { gdName },
      });
    } catch (error) {
      console.error("Error navigating to designs:", error);
      toast.error("Failed to load designer's designs");
    }
  };

  const handleActiveDeactive = async (id, status) => {
    try {
      const statusText = status === "active" ? "Deactivate" : "Activate";
      const Color = status === "active" ? "#DC3741" : "#1b55e2";

      const result = await Swal.fire({
        title: `Do you want to ${statusText} this Record?`,
        icon: "error",
        showCancelButton: true,
        confirmButtonColor: Color,
        confirmButtonText: statusText,
      });

      if (result.isConfirmed) {
        await axios.put(
          `${process.env.REACT_APP_URL}/admin/graphic_designer/${id}`,
          {
            status: "active",
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );
        toast.success(`Graphic designer Activated successfully`);
        fetchData();
      } else if (result.isDenied) {
        await axios.put(
          `${process.env.REACT_APP_URL}/admin/graphic_designer/${id}`,
          {
            status: "inactive",
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );
        toast.success(`Graphic designer Deactivated successfully`);
      }
    } catch (error) {
      console.error("Error updating graphic designer status:", error);
      toast.error("Failed to update graphic designer status");
    }
  };

  const handlePageClick = (data) => {
    setPage(data.selected + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const initialsOf = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const TINTS = [
    { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
    { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
    { bg: "var(--color-success-bg)", color: "var(--color-success-text)" },
    { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
  ];
  const tint = (s = "") => TINTS[(String(s).length) % TINTS.length];
  const fmt = (d) =>
    d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—";

  const [dense, setDense] = useState(false);
  const [sortDir, setSortDir] = useState("");
  const [isMobileView, setIsMobileView] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  let displayData = [...(data || [])];
  if (sortDir) {
    displayData.sort((a, b) => {
      const av = (a?.name || "").toLowerCase();
      const bv = (b?.name || "").toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }

  const StatusToggle = ({ item }) => (
    <button type="button" className={`al-toggle ${item.status === "active" ? "is-on" : ""}`}
      title={item.status === "active" ? "Deactivate" : "Activate"}
      onClick={() => handleActiveDeactive(item.user_id, item.status)}>
      <span className="al-toggle__knob" />
    </button>
  );

  const DesignsBtn = ({ item }) => (
    <button type="button" title="View Designs"
      onClick={() => fetchGDDesigns(item.user_id || item.id, item.name)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-primary)", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><circle cx="7" cy="8" r="1.5"/><line x1="10" y1="8" x2="18" y2="8"/><circle cx="7" cy="12" r="1.5"/><line x1="10" y1="12" x2="18" y2="12"/><circle cx="7" cy="16" r="1.5"/><line x1="10" y1="16" x2="18" y2="16"/></svg>
      Designs
    </button>
  );

  const RowActions = ({ item }) => (
    <div className="al-actions">
      <button type="button" className="al-act" title="View"
        onClick={() => Navigate(`/admin/graphic-designer/show/${item.user_id}`)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
      </button>
      <button type="button" className="al-act al-act--primary" title="Edit"
        onClick={() => Navigate(`/admin/graphic-designer/edit/${item.user_id}`)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
      </button>
      <button type="button" className="al-act al-act--danger" title="Delete"
        onClick={() => handleDelete(item.user_id)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
      </button>
    </div>
  );

  const GRID = { gridTemplateColumns: "56px 1.4fr 1.7fr 1.1fr .8fr .9fr 1.1fr 118px" };

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Graphic Designers</h1>
          <p className="al-sub">Design team members, their output and availability.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn al-btn--primary" onClick={() => Navigate("/admin/graphic-designer/store")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Designer
          </button>
        </div>
      </div>

      <div className="al-card">
        <div className="al-toolbar">
          <div className="al-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <input type="search" placeholder="Search designers by name, email…"
              onKeyDown={(e) => e.key === "Enter" && (setPage(1), fetchData())}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="al-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">Select Month</option>
              <option value="All">All</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>
          <button type="button" className="al-tool" style={{ marginLeft: "auto" }} onClick={() => setDense((d) => !d)} title="Toggle density">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {!isMobileView ? (
          <div className="al-tablewrap">
            <div style={{ minWidth: 1000, "--al-row-pad": dense ? "8px" : "13px" }}>
              <div className="al-thead" style={GRID}>
                <span className="al-th">ID</span>
                <span className="al-th al-th--sort" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : d === "desc" ? "" : "asc"))}>
                  Name
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: sortDir ? 1 : 0.45, transform: sortDir === "desc" ? "rotate(180deg)" : "none" }}><polyline points="18 15 12 9 6 15"/></svg>
                </span>
                <span className="al-th">Email</span>
                <span className="al-th">Phone</span>
                <span className="al-th">Designs</span>
                <span className="al-th">Status</span>
                <span className="al-th">Date &amp; Time</span>
                <span className="al-th al-th--right">Actions</span>
              </div>

              {loading ? (
                [...Array(6)].map((_, i) => (
                  <div className="al-row" style={GRID} key={i}>
                    {[...Array(8)].map((__, j) => (
                      <span key={j} className="al-skel" style={{ width: j === 0 ? 26 : "70%", height: 14 }} />
                    ))}
                  </div>
                ))
              ) : displayData && displayData.length > 0 ? (
                displayData.map((item) => {
                  const t = tint(item?.name);
                  return (
                    <div className="al-row" style={GRID} key={item.user_id}>
                      <span className="al-mono" style={{ color: "var(--color-primary)", fontWeight: 700 }}>{item.user_id}</span>
                      <div className="al-id">
                        <span className="al-avatar" style={{ background: t.bg, color: t.color }}>{initialsOf(item?.name)}</span>
                        <span className="al-name">{item?.name}</span>
                      </div>
                      <span className="al-muted al-ellipsis" title={item?.email}>{item?.email}</span>
                      <span className="al-mono al-muted">{item?.phone || "N/A"}</span>
                      <span><DesignsBtn item={item} /></span>
                      <span><StatusToggle item={item} /></span>
                      <span className="al-mono al-muted">{fmt(item?.created_at)}</span>
                      <RowActions item={item} />
                    </div>
                  );
                })
              ) : (
                <div className="al-empty">
                  <div className="al-empty__icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  </div>
                  <p className="al-empty__title">No Record Found</p>
                  <p className="al-empty__text">Try a different search or add a designer.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="al-mobile">
            {loading ? (
              <div className="al-empty"><p className="al-empty__title">Loading…</p></div>
            ) : displayData && displayData.length > 0 ? (
              displayData.map((item) => {
                const t = tint(item?.name);
                return (
                  <div className="al-mcard" key={item.user_id}>
                    <div className="al-mcard__top">
                      <span className="al-avatar" style={{ background: t.bg, color: t.color }}>{initialsOf(item?.name)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="al-name">{item?.name}</div>
                        <div className="al-mono al-muted" style={{ fontSize: 11 }}>ID {item.user_id}</div>
                      </div>
                      <StatusToggle item={item} />
                    </div>
                    <div className="al-mrow"><span className="al-muted">Email</span><span>{item?.email}</span></div>
                    <div className="al-mrow"><span className="al-muted">Phone</span><span className="al-mono">{item?.phone || "N/A"}</span></div>
                    <div className="al-mrow"><span className="al-muted">Designs</span><DesignsBtn item={item} /></div>
                    <div className="al-mrow"><span className="al-muted">Date</span><span className="al-mono al-muted">{fmt(item?.created_at)}</span></div>
                    <div className="al-mactions"><RowActions item={item} /></div>
                  </div>
                );
              })
            ) : (
              <div className="al-empty"><p className="al-empty__title">No Record Found</p></div>
            )}
          </div>
        )}

        <div className="al-foot">
          <span className="al-foot__info">Showing <strong>{displayData?.length || 0}</strong> designers</span>
          {data?.length !== 0 && (
            <ReactPaginate
              previousLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>}
              nextLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
              breakLabel={"..."}
              pageCount={totalpage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"} pageLinkClassName={"page-link"}
              previousClassName={"page-item"} previousLinkClassName={"page-link"}
              nextClassName={"page-item"} nextLinkClassName={"page-link"}
              breakClassName={"page-item"} breakLinkClassName={"page-link"}
              activeClassName={"active"} />
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphicDesignerList;
